import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FireSafetyParametersTable from "../../components/KPIScoreCard/FireSafetyParametersTable";
import ExternalAuditReportTable from "../../components/KPIScoreCard/ExternalAuditReportTable";
import InternalAuditReportTable from "../../components/KPIScoreCard/InternalAuditReportTable";
import AuditReportChart from "../../components/KPIScoreCard/AuditReportChart";
import FireSafetyParametersChart from "../../components/KPIScoreCard/FireSafetyParametersChart";
import axiosInstance from "../../common/axiosInstance";
import { DotLoader } from "../../components/loader/Loader";
import { usePremisesStore } from "../../store/store";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { Eye, EyeOff, XIcon, RefreshCw, Download } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useDropzone } from "react-dropzone";
import { FileAttachment } from "../../store/kpiStore";
import { Attachment } from "../../types/api";
import { toast } from "react-toastify";
import { S3TempUrl } from "../../utils/s3Client";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import FileUpload from "../../components/FileUpload/FileUpload";
import PdfViewer from "../../components/pdf/PdfViewer";
import {
  findMappingForParameter,
  matchesParticular,
} from "../../utils/misChecklistMapping";
import { FLSSafetySystemRow } from "../../components/MISChecklist/FLSSafetySystemsTable";
import { MEPFLSInfrastructureRow } from "../../components/MISChecklist/MEPFLSInfrastructureTable";
import { FireIncidentDrillsRow } from "../../components/MISChecklist/FireIncidentDrillsTable";
import {
  getInitialExternalAuditRecords,
  getInitialInternalAuditRecords,
  getInitialFireSafetyParameters,
  AuditRecord,
  FireSafetyParameter,
} from "../../utils/syncMISToKPI";
import ScorecardTrendsChart from "../../components/KPIScoreCard/ScorecardTrendsChart";

// Utility functions for file handling
const getFileExtension = (filename: string): string => {
  return filename.split(".").pop()?.toLowerCase() || "";
};

const canPreviewFile = (filename: string): boolean => {
  const extension = getFileExtension(filename);
  return ["pdf", "jpg", "jpeg", "png", "doc", "docx", "ppt", "pptx"].includes(extension);
};

interface FilePreviewState {
  url: string;
  name: string;
  type: string;
}


const KPIScoreCard: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id && id !== "new";
  const isNewMode = id === "new";
  const [isViewMode, setIsViewMode] = useState<boolean>(isEditMode ? true : false); // Start in view mode if editing existing
  const chartImageGetterRef = useRef<(() => string) | null>(null);
  const paramsChartImageGetterRef = useRef<(() => string) | null>(null);
  const trendsChartImageGetterRef = useRef<(() => string) | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [scorecardName, setScorecardName] = useState<string>("");
  const [scorecardDescription, setScorecardDescription] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [showChart, setShowChart] = useState<boolean>(false);
  const [showParametersChart, setShowParametersChart] = useState<boolean>(false);
  const [showTrendsChart, setShowTrendsChart] = useState<boolean>(false);
  const [files, setFiles] = useState<File[] | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    []
  );
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [maxFileSize, setMaxFileSize] = useState<number>(15);
  const [fileurl, setFileUrl] = useState<FilePreviewState>({
    url: "",
    name: "",
    type: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { getsignedurl } = S3TempUrl();
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 10,
    maxSize: 15 * 1024 * 1024, // 15MB
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".tiff", ".webp"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-excel": [".xls", ".xlsx"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-powerpoint": [".ppt", ".pptx"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
    },
  });

  const handleFilesChange = (newFiles: File[] | null) => {
    if (newFiles) {
      setFiles(newFiles);
    }
  };

  const handleRemoveExistingAttachment = (index: number) => {
    if (isViewMode) return;

    setExistingAttachments((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  // Function to handle file preview
  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split("/").pop() ||
      "";

    if (canPreviewFile(filename)) {
      try {
        setIsLoading(true);

        const s3Key = attachment.url?.split("/").pop();
        if (!s3Key) {
          throw new Error("Invalid file URL");
        }

        const fileurl = await getsignedurl(s3Key, getFileExtension(filename));
        const filobj = {
          url: fileurl,
          name: filename,
          type: getFileExtension(filename),
        };
        setFileUrl(filobj);
        setIsDocumentViewerOpen(true);
      } catch (err: any) {
        console.error("Error previewing file:", err);
        setErrorMessage("File not found or failed to load");
        toast.error("Failed to preview file");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.info(
        "Preview not available for this file type. Use download instead."
      );
    }
  };

  const { allPremises, fetchPremises } = usePremisesStore();

  // Initial template data - all editable fields start as null (N/A)
  // Using shared function from syncMISToKPI utility

  const [fireSafetyParameters, setFireSafetyParameters] = useState<
    FireSafetyParameter[]
  >(getInitialFireSafetyParameters());

  const [externalAuditRecords, setExternalAuditRecords] = useState<
    AuditRecord[]
  >(getInitialExternalAuditRecords());

  const [internalAuditRecords, setInternalAuditRecords] = useState<
    AuditRecord[]
  >(getInitialInternalAuditRecords());

  // ── Stored incident data ───
  const [storedNearMissActual, setStoredNearMissActual] = useState<number | null>(null);
  const [storedNearMissPct,    setStoredNearMissPct]    = useState<number | null>(null);
  const [storedMajorActual,    setStoredMajorActual]    = useState<number | null>(null);
  const [storedMajorPct,       setStoredMajorPct]       = useState<number | null>(null);

  // Fetch premises on mount
  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  // Auto-fill date with today's date when creating new scorecard
  useEffect(() => {
    if (isNewMode && !selectedDate) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      setSelectedDate(`${year}-${month}-${day}`);
    }
  }, [isNewMode, selectedDate]);

  // Sync Category A scores from audit tables to parameters
  useEffect(() => {
    // Find Category A from External Audit
    const externalCategoryA = externalAuditRecords.find((record) => record.category === 'A');
    if (externalCategoryA) {
      setFireSafetyParameters((prevParams) => {
        return prevParams.map((param) => {
          if (param.parameter === 'EXTERNAL AUDIT HIGH CATEGORY POINTS COMPLETED') {
            const score = externalCategoryA.currentScore;
            const inPercent = score !== null && param.maxScore > 0 ? score / param.maxScore : 0;
            const percentage = score !== null && param.maxScore > 0 ? (score / param.maxScore) * 100 : 0;
            return {
              ...param,
              score: score,
              inPercent: Math.round(inPercent * 100) / 100,
              percentage: Math.round(percentage * 100) / 100,
            };
          }
          return param;
        });
      });
    }

    // Find Category A from Internal Audit
    const internalCategoryA = internalAuditRecords.find((record) => record.category === 'A');
    if (internalCategoryA) {
      setFireSafetyParameters((prevParams) => {
        return prevParams.map((param) => {
          if (param.parameter === 'INTERNAL AUDIT HIGH CATEGORY POINTS COMPLETED') {
            const score = internalCategoryA.currentScore;
            const inPercent = score !== null && param.maxScore > 0 ? score / param.maxScore : 0;
            const percentage = score !== null && param.maxScore > 0 ? (score / param.maxScore) * 100 : 0;
            return {
              ...param,
              score: score,
              inPercent: Math.round(inPercent * 100) / 100,
              percentage: Math.round(percentage * 100) / 100,
            };
          }
          return param;
        });
      });
    }
  }, [externalAuditRecords, internalAuditRecords]);

  // Load scorecard data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadScorecard(id);
    } else if (isNewMode) {
      // Reset to initial values for new scorecard
      setFireSafetyParameters(getInitialFireSafetyParameters());
      setExternalAuditRecords(getInitialExternalAuditRecords());
      setInternalAuditRecords(getInitialInternalAuditRecords());
      setScorecardName("");
      setScorecardDescription("");
      setSelectedPremises("");
      setStoredNearMissActual(null);
      setStoredNearMissPct(null);
      setStoredMajorActual(null);
      setStoredMajorPct(null);
    }
  }, [id, isEditMode, isNewMode]);

  // ── Trends chart derived values ───────────────────────────────────────────
  const extACatNcPct = externalAuditRecords.find(r => r.category === 'A')?.completionPercent ?? null;
  const intACatNcPct = internalAuditRecords.find(r => r.category === 'A')?.completionPercent ?? null;
  const totalScore = fireSafetyParameters.reduce((s, p) => s + (p.score ?? 0), 0);
  const totalMax = fireSafetyParameters.reduce((s, p) => s + p.maxScore, 0);
  const avgPct = totalMax > 0 ? (totalScore / totalMax) * 100 : null;

  // ── Near Miss and Major Incident ─────────────────────────────────────────
  // Prefer stored values from MIS sync; fall back to sr.No derivation for
  // older scorecards that were saved before the incident fields were added.
  // const sr60 = fireSafetyParameters.find(p => p.misChecklistSrNos === 60);
  // const sr61 = fireSafetyParameters.find(p => p.misChecklistSrNos === 61);

  const nearMissActual = storedNearMissActual ?? null;
  const nearMissPct    = storedNearMissPct    ?? null;

  const majorActual    = storedMajorActual ?? null;
  const majorPct       = storedMajorPct ?? null;

  // const incidentActual = (nearMissActual ?? 0) + (majorActual ?? 0);
  // const incidentTarget = (sr60?.maxScore ?? 0) + (sr61?.maxScore ?? 0);
  // const incidentPct = incidentTarget > 0 ? (incidentActual / incidentTarget) * 100 : null;

  const loadScorecard = async (scorecardId: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/fire-safety-scorecards/${scorecardId}`
      );
      const data = response.data?.data || response.data;

      if (data) {
        setScorecardName(data.name || "");
        setScorecardDescription(data.description || "");
        setSelectedPremises(data.premisesId || "");
        setSelectedDate(data.date || "");

        if (
          data.fireSafetyParameters &&
          Array.isArray(data.fireSafetyParameters)
        ) {
          setFireSafetyParameters(data.fireSafetyParameters);
        }

        if (
          data.externalAuditRecords &&
          Array.isArray(data.externalAuditRecords)
        ) {
          setExternalAuditRecords(data.externalAuditRecords);
        }

        if (
          data.internalAuditRecords &&
          Array.isArray(data.internalAuditRecords)
        ) {
          setInternalAuditRecords(data.internalAuditRecords);
        }

        // ── Load stored incident data if available (saved by MIS sync) ────
        if (data.nearMissActual !== undefined) setStoredNearMissActual(data.nearMissActual ?? null);
        if (data.nearMissPct    !== undefined) setStoredNearMissPct(data.nearMissPct    ?? null);
        if (data.majorActual    !== undefined) setStoredMajorActual(data.majorActual    ?? null);
        if (data.majorPct       !== undefined) setStoredMajorPct(data.majorPct          ?? null);
      }
    } catch (error: any) {
      console.error("Error loading scorecard:", error);
      alert(error.response?.data?.message || "Failed to load scorecard");
      navigate("/safety-equipment/fire-safety-scorecard");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!scorecardName.trim()) {
      alert("Please enter a scorecard name");
      return;
    }

    if (!selectedPremises) {
      alert("Please select a premises. A specific premises is required to save this scorecard.");
      return;
    }

    try {
      setSaving(true);
      // Get premises name
      const selectedPremise = allPremises.find(
        (p: any) => p._id === selectedPremises
      );
      const premisesName = selectedPremise?.premisesName || "";

      const scorecardData = {
        name: scorecardName.trim(),
        description: scorecardDescription.trim(),
        premisesId: selectedPremises || null,
        premisesName: premisesName,
        date: selectedDate || null,
        fireSafetyParameters,
        externalAuditRecords,
        internalAuditRecords,
      };

      if (isEditMode && id) {
        // Update existing
        await axiosInstance.put(`/api/fire-safety-scorecards/${id}`, scorecardData);
        alert("Scorecard updated successfully!");
      } else {
        // Create new
        await axiosInstance.post("/api/fire-safety-scorecards", scorecardData);
        alert("Scorecard created successfully!");
      }

      navigate("/safety-equipment/fire-safety-scorecard");
    } catch (error: any) {
      console.error("Error saving scorecard:", error);
      alert(error.response?.data?.message || "Failed to save scorecard");
    } finally {
      setSaving(false);
    }
  };

  const handleParameterChange = useCallback(
    (index: number, updatedRow: FireSafetyParameter) => {
      setFireSafetyParameters((prev) => {
        const newData = [...prev];
        newData[index] = updatedRow;
        return newData;
      });
    },
    []
  );

  const handleExternalAuditChange = useCallback(
    (index: number, updatedRecord: AuditRecord) => {
      setExternalAuditRecords((prev) => {
        const newData = [...prev];
        newData[index] = updatedRecord;

        // Update Fire Safety Parameters if Category A was updated
        if (updatedRecord.category === 'A') {
          setFireSafetyParameters((prevParams) => {
            return prevParams.map((param) => {
              if (param.parameter === 'EXTERNAL AUDIT HIGH CATEGORY POINTS COMPLETED') {
                const score = updatedRecord.currentScore;
                const inPercent = score !== null && param.maxScore > 0 ? score / param.maxScore : 0;
                const percentage = score !== null && param.maxScore > 0 ? (score / param.maxScore) * 100 : 0;
                return {
                  ...param,
                  score: score,
                  inPercent: Math.round(inPercent * 100) / 100,
                  percentage: Math.round(percentage * 100) / 100,
                };
              }
              return param;
            });
          });
        }

        return newData;
      });
    },
    []
  );

  const handleInternalAuditChange = useCallback(
    (index: number, updatedRecord: AuditRecord) => {
      setInternalAuditRecords((prev) => {
        const newData = [...prev];
        newData[index] = updatedRecord;

        // Update Fire Safety Parameters if Category A was updated
        if (updatedRecord.category === 'A') {
          setFireSafetyParameters((prevParams) => {
            return prevParams.map((param) => {
              if (param.parameter === 'INTERNAL AUDIT HIGH CATEGORY POINTS COMPLETED') {
                const score = updatedRecord.currentScore;
                const inPercent = score !== null && param.maxScore > 0 ? score / param.maxScore : 0;
                const percentage = score !== null && param.maxScore > 0 ? (score / param.maxScore) * 100 : 0;
                return {
                  ...param,
                  score: score,
                  inPercent: Math.round(inPercent * 100) / 100,
                  percentage: Math.round(percentage * 100) / 100,
                };
              }
              return param;
            });
          });
        }

        return newData;
      });
    },
    []
  );

  const handleDownloadPDF = async () => {
    const wasAuditHidden = !showChart;
    const wasParamsHidden = !showParametersChart;
    const wasTrendsHidden = !showTrendsChart;

    if (wasAuditHidden) setShowChart(true);
    if (wasParamsHidden) setShowParametersChart(true);
    if (wasTrendsHidden) setShowTrendsChart(true);
    // Wait longer when multiple charts need to mount and render
    await new Promise(r => setTimeout(r, 600));

    try {
      const doc = new jsPDF({ orientation: "landscape" });
      const premiseName = allPremises.find((p: any) => p._id === selectedPremises)?.premisesName || "N/A";

      doc.setFontSize(18);
      doc.setTextColor(0, 168, 165);
      doc.text("Fire Safety Scorecard Report", 14, 18);
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Name: ${scorecardName || "N/A"}`, 14, 26);
      doc.text(`Premises: ${premiseName}`, 14, 32);
      doc.text(`Date: ${selectedDate || "N/A"}`, 14, 38);

      let currentY = 44;
      //Heading Fire Safety Scorecard Trends Chart    
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(255, 100, 0);
      doc.rect(14, currentY, doc.internal.pageSize.width - 28, 8, "F");
      doc.text("Fire Safety Scorecard Trends Chart", 16, currentY + 6);
      currentY += 10;

      // ── Trends chart image (always first, no section header needed) ───────
      if (trendsChartImageGetterRef.current) {
        try {
          const imgData = trendsChartImageGetterRef.current();
          if (imgData) {
            const pageWidth = doc.internal.pageSize.width;
            const imgWidth = pageWidth - 28;
            const imgHeight = imgWidth * (320 / 860); // chart is h-80 = shorter
            doc.addImage(imgData, "PNG", 14, currentY, imgWidth, imgHeight);
            currentY += imgHeight + 8;
          }
        } catch (e) {
          console.warn("Trends chart image not available:", e);
        }
      }

      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(255, 100, 0);
      doc.rect(14, currentY, doc.internal.pageSize.width - 28, 8, "F");
      doc.text("Audit Report chart", 16, currentY + 6);
      currentY += 10;

      // ── Audit Report chart image ──────────────────────────────────────────
      if (chartImageGetterRef.current) {
        try {
          const imgData = chartImageGetterRef.current();
          if (imgData) {
            const pageWidth = doc.internal.pageSize.width;
            const imgWidth = pageWidth - 28;
            const imgHeight = imgWidth * (440 / 860);
            if (currentY + imgHeight > doc.internal.pageSize.height - 20) {
              doc.addPage(); currentY = 14;
            }
            doc.addImage(imgData, "PNG", 14, currentY, imgWidth, imgHeight);
            currentY += imgHeight + 6;
          }
        } catch (e) {
          console.warn("Audit chart image not available:", e);
        }
      }

      if (currentY > doc.internal.pageSize.height - 40) { doc.addPage(); currentY = 14; }

      // ── External Audit header ─────────────────────────────────────────────
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(255, 100, 0);
      doc.rect(14, currentY, doc.internal.pageSize.width - 28, 8, "F");
      doc.text("EXTERNAL AUDIT - % OF NON COMPLIANCE STATUS REPORT", 16, currentY + 6);
      currentY += 10;
      autoTable(doc, {
        startY: currentY,
        head: [["Category", "Total NC", "Completion NC", "Pending", "Completion %", "Current Score", "Max Score"]],
        body: externalAuditRecords.map((r) => [
          `Category ${r.category}`,
          r.totalNC ?? "N/A",
          r.completionNC ?? "N/A",
          r.pending ?? "N/A",
          r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A",
          r.currentScore != null ? r.currentScore.toFixed(2) : "N/A",
          r.maxScore,
        ]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [255, 100, 0] },
      });

      let y = (doc as any).lastAutoTable.finalY + 8;

      // ── Internal Audit ────────────────────────────────────────────────────
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(180, 0, 120);
      doc.rect(14, y, doc.internal.pageSize.width - 28, 8, "F");
      doc.text("INTERNAL AUDIT - % OF NON COMPLIANCE STATUS REPORT", 16, y + 6);
      y += 10;

      autoTable(doc, {
        startY: y,
        head: [["Category", "Total NC", "Completion NC", "Pending", "Completion %", "Current Score", "Max Score"]],
        body: internalAuditRecords.map((r) => [
          `Category ${r.category}`,
          r.totalNC ?? "N/A",
          r.completionNC ?? "N/A",
          r.pending ?? "N/A",
          r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A",
          r.currentScore != null ? r.currentScore.toFixed(2) : "N/A",
          r.maxScore,
        ]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [180, 0, 120] },
      });

      y = (doc as any).lastAutoTable.finalY + 8;

      // ── Fire Safety Parameters ────────────────────────────────────────────
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(0, 168, 165);
      doc.rect(14, y, doc.internal.pageSize.width - 28, 8, "F");
      doc.text("PARAMETERS OF FIRE SAFETY", 16, y + 6);
      y += 10;

      if (paramsChartImageGetterRef.current) {
        try {
          const imgData = paramsChartImageGetterRef.current();
          if (imgData) {
            const pageWidth = doc.internal.pageSize.width;
            const imgWidth = pageWidth - 28;
            const imgHeight = imgWidth * (600 / 860);
            if (y + imgHeight > doc.internal.pageSize.height - 20) { doc.addPage(); y = 14; }
            doc.addImage(imgData, "PNG", 14, y, imgWidth, imgHeight);
            y += imgHeight + 6;
          }
        } catch (e) {
          console.warn("Parameters chart image not available:", e);
        }
      }

      if (y > doc.internal.pageSize.height - 40) { doc.addPage(); y = 14; }

      const fsTotalScore = fireSafetyParameters.reduce((s, p) => s + (p.score ?? 0), 0);
      const fsTotalMax = fireSafetyParameters.reduce((s, p) => s + p.maxScore, 0);

      autoTable(doc, {
        startY: y,
        head: [["Parameter", "In %", "Score", "Max Score", "Percentage"]],
        body: [
          ...fireSafetyParameters.map((p) => [
            p.parameter,
            p.inPercent != null ? p.inPercent.toFixed(2) : "N/A",
            p.score != null ? p.score.toFixed(2) : "N/A",
            p.maxScore.toFixed(2),
            p.percentage != null ? `${p.percentage.toFixed(2)}%` : "N/A",
          ]),
          ["TOTAL", "", fsTotalScore.toFixed(2), fsTotalMax.toFixed(2),
            fsTotalMax > 0 ? `${((fsTotalScore / fsTotalMax) * 100).toFixed(2)}%` : "N/A"],
        ],
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [0, 168, 165] },
        didParseCell: (data) => {
          if (data.row.index === fireSafetyParameters.length) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [255, 235, 150];
          }
        },
      });

      doc.save(`Fire_Safety_Scorecard_${(scorecardName || "Report").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);

    } catch (err) {
      console.error("PDF error:", err);
      alert("Failed to generate PDF.");
    } finally {
      if (wasAuditHidden) setShowChart(false);
      if (wasParamsHidden) setShowParametersChart(false);
      if (wasTrendsHidden) setShowTrendsChart(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <DotLoader />
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-4">
      <div className="w-full mt-20 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {isEditMode ? "Edit" : isNewMode ? "Create New" : ""} Fire Safety
              Scorecard
            </h2>
            <p className="text-gray-600">
              {isNewMode
                ? "Fill in the fields below and save to create a new scorecard"
                : "View and manage fire safety parameters, audit reports, and compliance scores"}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/safety-equipment/kpi-scorecard")}
              className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
            {isEditMode && (
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors shadow-lg"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </button>
            )}
            {isEditMode && (
              <button
                onClick={() => setIsViewMode(!isViewMode)}
                className="flex items-center gap-2 py-2 px-4 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                {isViewMode ? (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit Mode
                  </>
                ) : (
                  <>
                    <Eye className="w-5 h-5" />
                    View Mode
                  </>
                )}
              </button>
            )}
            {(isEditMode || isNewMode) && !isViewMode && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {isEditMode ? "Update" : "Save"} Scorecard
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {(isEditMode || isNewMode) && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scorecard Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={scorecardName}
                onChange={(e) => setScorecardName(e.target.value)}
                placeholder="Enter scorecard name"
                disabled={isViewMode}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={scorecardDescription}
                onChange={(e) => setScorecardDescription(e.target.value)}
                placeholder="Enter description (optional)"
                disabled={isViewMode}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Premises <span className="text-red-500">*</span>
              </label>
              <div className={isViewMode ? 'pointer-events-none opacity-60' : ''}>
                <Dropdown
                  selectedValue={selectedPremises}
                  onChange={setSelectedPremises}
                  type="premises"
                />
              </div>
              {!isViewMode && !selectedPremises && (
                <p className="text-xs text-amber-600 mt-1">⚠ A specific premises must be selected</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={isViewMode}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
              />
            </div>
          </div>
          <FileUpload
            mode={isViewMode ? "view" : (isEditMode ? "update" : "create")}
            files={files}
            onFilesChange={handleFilesChange}
            existingAttachments={existingAttachments}
            onRemoveExistingAttachment={handleRemoveExistingAttachment}
            onPreviewFile={handlePreviewFile}
            uploading={uploading}
            uploadProgress={uploadProgress}
            disabled={isDisabled || isViewMode}
            maxFileSizeMB={maxFileSize}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
          />
        </div>
      )}

      <div className="flex flex-col gap-6">
        {/* ── Show Trends chart ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between mx-3 mb-2">
          <h3 className="text-lg font-semibold text-gray-800">
            Trends chart
          </h3>
          <button
            onClick={() => setShowTrendsChart(!showTrendsChart)}
            className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors text-sm font-medium"
          >
            {showTrendsChart ? (
              <>
                <EyeOff className="w-4 h-4" />
                Hide Trends Graph
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Show Trends Graph
              </>
            )}
          </button>
        </div>
        {showTrendsChart &&
          (<div className="mb-6 border-t border-gray-200 pt-6">
            <ScorecardTrendsChart
              extACatNcPct={extACatNcPct}
              intACatNcPct={intACatNcPct}
              avgPct={avgPct}
              nearMissPct={nearMissPct}
              nearMissActual={nearMissActual}
              majorPct={majorPct}
              majorActual={majorActual}
              reportDate={selectedDate}
              premisesName={allPremises.find((p: any) => p._id === selectedPremises)?.premisesName}
              onCanvasReady={(getImage) => { trendsChartImageGetterRef.current = getImage; }}
            />
          </div>)
        }
        {/* External Audit Report Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                External Audit Report
              </h3>
              <p className="text-xs text-gray-500 mt-1">Data is filled from MIS Checklist and is read-only here.</p>
            </div>
            <button
              onClick={() => setShowChart(!showChart)}
              className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors text-sm font-medium"
            >
              {showChart ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  Hide Graph
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Show Graph
                </>
              )}
            </button>
          </div>
          {showChart && (
            <div className="mb-6 border-t border-gray-200 pt-6">
              <AuditReportChart
                externalRecords={externalAuditRecords}
                internalRecords={internalAuditRecords}
                onCanvasReady={(getImage) => { chartImageGetterRef.current = getImage; }}
              />
            </div>
          )}
          <ExternalAuditReportTable
            records={externalAuditRecords}
            onChange={handleExternalAuditChange}
            isEditable={false}
          />
        </div>

        {/* Internal Audit Report Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Internal Audit Report
            </h3>
            <p className="text-xs text-gray-500 mb-4">Data is filled from MIS Checklist and is read-only here.</p>
          </div>
          <InternalAuditReportTable
            records={internalAuditRecords}
            onChange={handleInternalAuditChange}
            isEditable={false}
          />
        </div>

        {/* Fire Safety Parameters Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800">
              Parameters of Fire Safety
            </h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowParametersChart(!showParametersChart)}
                className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors text-sm font-medium"
              >
                {showParametersChart ? (
                  <>
                    <EyeOff className="w-4 h-4" />
                    Hide Graph
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    Show Graph
                  </>
                )}
              </button>
            </div>
          </div>
          {showParametersChart && (
            <div className="mb-6 border-t border-gray-200 pt-6">
              <FireSafetyParametersChart
                parameters={fireSafetyParameters}
                reportName={scorecardName}
                reportDate={selectedDate}
                premisesName={allPremises.find((p: any) => p._id === selectedPremises)?.premisesName}
                onCanvasReady={(getImage) => { paramsChartImageGetterRef.current = getImage; }}
              />
            </div>
          )}
          <FireSafetyParametersTable
            data={fireSafetyParameters}
            onChange={handleParameterChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
          />
        </div>
      </div>

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">
                Document Preview: {fileurl.name}
              </h3>
              <button
                onClick={() => {
                  setIsDocumentViewerOpen(false);
                  setFileUrl({ url: "", name: "", type: "" });
                  setErrorMessage("");
                }}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Close document viewer"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {errorMessage}
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : fileurl.url ? (
                fileurl.type === "pdf" ? (
                  <div className="w-full h-full">
                    <PdfViewer fileUrl={fileurl.url} />
                  </div>
                ) : (
                  <div className="w-full h-full">
                    <DocumentViewer file={fileurl.url} name={fileurl.name} />
                  </div>
                )
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p>No document to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIScoreCard;
